import {
  Banner,
  Button,
  AlertIcon,
  Flex,
  Modal,
  Skeleton,
  Typography,
  useToast,
  TrashIcon,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import { useMaskito } from '@maskito/react';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import { SwitchWithBorder } from '@jane/business-admin/components/SwitchWithBorder';
import type { UpdateCarouselBannerParams } from '@jane/business-admin/data-access';
import {
  useStoreMenuMetadata,
  useStoreSettings,
  useStores,
  useUpdateCarouselBanner,
} from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useDebouncedTrack,
  useModalActionsWithTracking,
  useStoreId,
} from '@jane/business-admin/hooks';
import {
  ErrorReasons,
  EventNames,
  ModalNames,
  SearchSubjects,
  SettingNames,
  isValidUrl,
  normalizePath,
  parseValidationErrors,
  track,
  urlMask,
} from '@jane/business-admin/util';
import type {
  EditedStoreMenuConfig,
  FilterNames,
} from '@jane/business-admin/util';
import { StoreSelectModal } from '@jane/shared-b2b/components';

import { CarouselSlidesManager } from './CarouselSlidesManager';
import type { CarouselSlide } from './CarouselSlidesManager';
import { ImageUploadArea } from './ImageUploadArea';
import { parseCarouselBanners, updateSlide } from './slideUtils';

import styles from './CarouselModal.module.css';

const FORM_ERROR_NAME = 'carousel-modal-errors';

const INVALID_LINK_MESSAGE = 'Enter a valid URL (e.g., https://example.com)';

const SLIDE_ERRORS_MESSAGE =
  'Fix issues on any slide marked with an error icon.';

const isInvalidLink = (link?: string) => !!link && !isValidUrl(link);

// Each slide's input needs its own useMaskito instance; reusing one across
// remounted inputs drops the mask on every other slide.
const SlideLinkField = ({
  link,
  onChange,
}: {
  link?: string;
  onChange: (link: string) => void;
}) => {
  const urlRef = useMaskito({ options: urlMask });

  return (
    <Form.TextField
      typography="body-regular"
      name="currentSlideLink"
      label="Link (Optional)"
      placeholder="https://"
      defaultValue={link || ''}
      value={link || ''}
      onChange={onChange}
      ref={urlRef}
      validate={(value) =>
        !isInvalidLink(value as string) || INVALID_LINK_MESSAGE
      }
    />
  );
};

const defaultAltText = (storeName?: string) => `${storeName} Marketing Banner`;

export const CarouselModal = ({ closeModal }: { closeModal: () => void }) => {
  const { pathname } = useLocation();
  const storeId = useStoreId();
  const { isFetched: menuDataFetched, data: menuData } =
    useStoreMenuMetadata(storeId);
  const { data: storePayload, isFetching: storeSettingsLoading } =
    useStoreSettings(storeId);
  const {
    mutateAsync: updateCarouselBanner,
    isSuccess: updateCarouselBannerSuccess,
  } = useUpdateCarouselBanner(storeId);

  const catchSubmitErrors = useCatchErrorsWithManager(
    'Error updating carousel banner. Please try again'
  );

  const toast = useToast();
  const stores: (number | string)[] = [];
  const formMethods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      [FORM_ERROR_NAME]: '',
      currentSlideLink: '',
      enabled: menuData?.carousel_banner
        ? menuData?.carousel_banner?.some((banner) => banner.visible)
        : true,
      slides: [] as CarouselSlide[],
      stores,
    },
  });
  const {
    formState: { dirtyFields, isValid },
    setValue,
    clearErrors,
    setError,
    trigger,
  } = formMethods;

  // Initialize with one default slide
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [initialSlides, setInitialSlides] = useState<CarouselSlide[]>([]);
  const [selectedSlidePosition, setSelectedSlidePosition] = useState(1);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isPublishingTo, setIsPublishingTo] = useState(false);

  // ------------ Store Selector -----------
  const {
    modalOpen: publishToModalOpen,
    openModal: setpublishToModalOpen,
    closeModal: setPublishToModalClose,
  } = useModalActionsWithTracking(ModalNames.CarouselPublishTo);

  const onFilterChange = (filterName: FilterNames, value?: string) => {
    if (!value) {
      track({
        event: EventNames.DeselectedFilter,
        filter_name: filterName,
        all_selected_values: [],
        deselected_value: 'all',
        modal_name: ModalNames.CarouselPublishTo,
        url: normalizePath(pathname, storeId),
      });
      return;
    }

    track({
      event: EventNames.SelectedFilter,
      filter_name: filterName,
      all_selected_values: [value],
      selected_value: value,
      modal_name: ModalNames.CarouselPublishTo,
      url: normalizePath(pathname, storeId),
    });
  };

  const onToggleAllStores = (selected: boolean) => {
    track({
      event: selected
        ? EventNames.SelectedCheckbox
        : EventNames.DeselectedCheckbox,
      checkbox_label: selected ? 'Select all stores' : 'Deselect all stores',
      modal_name: ModalNames.CarouselPublishTo,
    });
  };

  const onToggleStore = (storeId: string, selected: boolean) => {
    track({
      event: selected
        ? EventNames.SelectedCheckbox
        : EventNames.DeselectedCheckbox,
      checkbox_label: `${selected ? 'Select' : 'Deselect'} store ID ${storeId}`,
      modal_name: ModalNames.CarouselPublishTo,
    });
  };

  // -----------------------

  const selectedSlide = useMemo(
    () => slides.find((slide) => slide.position === selectedSlidePosition),
    [slides, selectedSlidePosition]
  );

  const selectedSlideHasError = selectedSlide?.hasError;
  const selectedSlideLink = selectedSlide?.link;

  useEffect(() => {
    if (selectedSlideHasError)
      setValue('currentSlideLink', selectedSlideLink ?? '', {
        shouldValidate: true,
      });
  }, [
    selectedSlidePosition,
    selectedSlideHasError,
    selectedSlideLink,
    setValue,
  ]);

  useEffect(() => {
    if (!menuDataFetched) return;

    const { slides: parsedSlides, isEnabled: parsedEnabled } =
      parseCarouselBanners(menuData?.carousel_banner ?? []);

    const areAllSlidesVisible = !!menuData?.carousel_banner.every(
      (slide) => slide.visible
    );

    const slidesWithErrors = parsedSlides.map((slide) => ({
      ...slide,
      hasError: isInvalidLink(slide.link),
    }));

    setSlides(slidesWithErrors);
    setInitialSlides(slidesWithErrors);

    setValue('enabled', parsedEnabled && areAllSlidesVisible);
    setIsEnabled(parsedEnabled && areAllSlidesVisible);
  }, [menuDataFetched]);

  const clearFormErrors = () => {
    clearErrors(FORM_ERROR_NAME);
  };

  const setFormError = (message: string) =>
    setError(FORM_ERROR_NAME, { type: 'manual', message });

  const patchCurrentSlide = (patch: Partial<CarouselSlide>) => {
    const updatedSlides = updateSlide(slides, selectedSlidePosition, patch);
    setSlides(updatedSlides);
    setValue('slides', updatedSlides, { shouldDirty: true });
    clearFormErrors();
  };

  const handleSlidesChange = (newSlides: CarouselSlide[]) => {
    setSlides(newSlides);
    setValue('slides', newSlides, { shouldDirty: true });
    clearFormErrors();
  };

  const handleSelectedSlideChange = (slidePosition: number) => {
    setSelectedSlidePosition(slidePosition);
    clearFormErrors();
  };

  const handleDesktopImageChange = (imageUrl: string) => {
    trigger();
    patchCurrentSlide({ desktopImage: imageUrl });
  };

  const handleDesktopImageRemove = () => {
    patchCurrentSlide({ desktopImage: undefined });
  };

  const handleMobileImageChange = (imageUrl: string) => {
    trigger();
    patchCurrentSlide({ mobileImage: imageUrl });
  };

  const handleMobileImageRemove = () => {
    patchCurrentSlide({ mobileImage: undefined, mobileId: undefined });
  };

  const handleLinkChange = (link: string) => {
    patchCurrentSlide({
      link: link || undefined,
      hasError: isInvalidLink(link),
    });
    setValue('currentSlideLink', link, { shouldValidate: true });
  };

  const handleAltTextChange = (altText: string) => {
    patchCurrentSlide({ altText });
  };

  const handleRemoveSlide = (slidePosition: number) => {
    if (!slides || slides.length <= 1) return;

    const updatedSlides = slides
      .filter((slide) => slide.position !== slidePosition)
      .map((slide, index) => ({ ...slide, position: index + 1 }));

    setSlides(updatedSlides);

    if (slidePosition === selectedSlidePosition) {
      setSelectedSlidePosition(updatedSlides[0]?.position || 1);
    }
    clearFormErrors();

    track({
      event: EventNames.ModifiedSetting,
      setting_name: SettingNames.CarouselBanner,
      action: 'remove_slide',
      modal_name: ModalNames.Carousel,
      revert: false,
    });
  };

  // ------------ Form Logic ------------
  const validateForm = () => {
    if (slides.some((slide) => isInvalidLink(slide.link))) {
      throw new FormValidationError(FORM_ERROR_NAME, [
        { name: FORM_ERROR_NAME, message: SLIDE_ERRORS_MESSAGE },
      ]);
    }

    const slidesWithoutImages = slides.flatMap((slide, index) =>
      slide.desktopImage ? [] : [index]
    );
    if (slidesWithoutImages && slidesWithoutImages.length > 0) {
      throw new FormValidationError(FORM_ERROR_NAME, [
        {
          name: FORM_ERROR_NAME,
          message: `Please provide a desktop image for slide${
            slidesWithoutImages.length > 1 ? 's' : ''
          } ${slidesWithoutImages
            .map((i) => i + 1)
            .join(', ')} to publish the carousel.`,
        },
      ]);
    }
  };

  const gatherData = (): UpdateCarouselBannerParams => {
    const carouselBanners: UpdateCarouselBannerParams['carousel_banners'] = [];

    slides.forEach((slide) => {
      if (slide.desktopImage || slide.desktopId) {
        carouselBanners.push({
          id: slide.desktopId,
          position: slide.position,
          breakpoint: 'desktop',
          visible: isEnabled,
          alt_text: slide.altText || defaultAltText(storePayload?.store.name),
          url: slide.link,
          ...(slide.desktopImage?.startsWith('data') && {
            file: slide.desktopImage,
          }),
        });
      }
      if (slide.mobileImage || slide.mobileId) {
        carouselBanners.push({
          id: slide.mobileId,
          position: slide.position,
          breakpoint: 'mobile',
          visible: isEnabled,
          alt_text: slide.altText || defaultAltText(storePayload?.store.name),
          url: slide.link,
          ...(slide.mobileImage?.startsWith('data') && {
            file: slide.mobileImage,
          }),
        });
      }
    });

    // Determine deleted banners by comparing existing API data with current slides
    const existingBanners = menuData?.carousel_banner ?? [];
    const currentIds = new Set<number>(
      carouselBanners.map((b) => (b.id ? b.id : -1)).filter((id) => id !== -1)
    );
    const deletedBannerIds: number[] = existingBanners
      .filter((b) => b.id && !currentIds.has(b.id))
      .map((b) => b.id!);

    return {
      carousel_banners:
        carouselBanners.length > 0 ? carouselBanners : undefined,
      deleted_carousel_banners:
        deletedBannerIds.length > 0 ? deletedBannerIds : undefined,
    };
  };

  const onSubmit = useCallback(() => {
    validateForm();
    const requestData = gatherData();

    const trackSubmit = (eventProps: Partial<EditedStoreMenuConfig> = {}) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        modal_name: ModalNames.Carousel,
        setting_name: 'Carousel',
        action: 'update',
        url: normalizePath(pathname, storeId),
        changed_attributes: Object.keys(dirtyFields),
        successful: true,
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      await updateCarouselBanner(requestData);
      trackSubmit();
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.InvalidParams,
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        trackSubmit({
          successful: false,
          error_reason: ErrorReasons.ServerError,
        });
        throw new Error('Error updating carousel banner. Please try again');
      },
    });
  }, [
    slides,
    isEnabled,
    dirtyFields,
    storeId,
    pathname,
    catchSubmitErrors,
    menuData?.carousel_banner,
    updateCarouselBanner,
  ]);

  const onSelectStores = (storeIds: number[]) => {
    // NOTE: Initial validation done as part of opening modal

    setValue('stores', storeIds, { shouldDirty: true });
    const requestData = gatherData();
    requestData.stores = storeIds;

    const trackSelectStores = (
      eventProps: Partial<EditedStoreMenuConfig> = {}
    ) => {
      track({
        event: EventNames.EditedStoreMenuConfig,
        modal_name: ModalNames.CarouselPublishTo,
        setting_name: 'Carousel Banner',
        action: 'update',
        url: normalizePath(pathname, storeId),
        changed_attributes: Object.keys(dirtyFields),
        successful: true,
        ...eventProps,
      });
    };

    const submitMethod = async () => {
      setIsPublishingTo(true);

      try {
        await updateCarouselBanner(requestData);
        trackSelectStores();
      } finally {
        setIsPublishingTo(false);
        setPublishToModalClose();
      }
    };

    return catchSubmitErrors({
      submitMethod,
      requestData,
      onValidationError: (validationErrors: Record<string, unknown>) => {
        trackSelectStores({
          successful: false,
          error_reason: ErrorReasons.InvalidParams,
        });
        throw new FormValidationError(
          FORM_ERROR_NAME,
          parseValidationErrors(validationErrors)
        );
      },
      callback: () => {
        trackSelectStores({
          successful: false,
          error_reason: ErrorReasons.ServerError,
        });

        setFormError('Error updating carousel banner. Please try again');
      },
    });
  };

  useEffect(() => {
    if (updateCarouselBannerSuccess) {
      toast.add({
        label: 'Carousel banner updated.',
        variant: 'success',
      });
      closeModal();
    }
  }, [closeModal, toast, updateCarouselBannerSuccess]);

  const formErrorMessage =
    (formMethods.formState?.errors?.[FORM_ERROR_NAME]?.message as string) ||
    (slides.some((slide) => slide.hasError) ? SLIDE_ERRORS_MESSAGE : undefined);

  const hasChanges = useMemo(
    () => !isEqual(initialSlides, slides),
    [slides, initialSlides]
  );

  const {
    data: storesData,
    isFetching: isFetchingStores,
    isPending: isLoadingStores,
  } = useStores();
  const currentStoreSelection =
    storePayload == null ? [] : [storePayload.store.id.toString()];
  const trackSearch = useDebouncedTrack(1000);

  return (
    <ConfirmWrapperWithTracking
      open={true}
      variant="full-screen"
      setOpen={() => closeModal()}
      hasChanges={hasChanges}
      modalName={ModalNames.Carousel}
    >
      <Form.BaseForm
        name="carousel modal"
        onSubmit={onSubmit}
        formMethods={formMethods}
        formErrorName={FORM_ERROR_NAME}
      >
        <Modal.Header
          title="Carousel banner"
          subtitle={storeSettingsLoading ? '' : storePayload?.store.name}
          actions={
            <Flex alignItems="center" gap={16}>
              <SwitchWithBorder
                name="enabled"
                label="Visible"
                onChange={(visible) => {
                  setIsEnabled(visible);
                  clearFormErrors();
                  track({
                    event: EventNames.ToggleVisibility,
                    final_state: visible ? 'visible' : 'hidden',
                    object: 'carousel banner',
                    successful: true,
                    trigger_source_id: 'carousel banner workspace',
                  });
                }}
              />
              <Button
                variant="secondary"
                label="Publish to"
                onClick={() => {
                  try {
                    validateForm();
                    setpublishToModalOpen();
                  } catch (error) {
                    if (error instanceof FormValidationError) {
                      setFormError(error.errors[0].message);
                    } else {
                      throw error;
                    }
                  }
                }}
                disabled={!isValid || !!formErrorMessage}
              />
              <Form.SubmitButton
                variant="primary"
                label="Publish"
                disabled={!!formErrorMessage}
              />
            </Flex>
          }
        />
        <Modal.Content>
          <Flex flexDirection="column" gap={24}>
            {formErrorMessage && (
              <Banner
                variant="error"
                label={formErrorMessage}
                icon={<AlertIcon />}
              />
            )}

            {menuDataFetched && slides && selectedSlide ? (
              <Flex gap={40}>
                <Flex
                  flexDirection="column"
                  gap={16}
                  width={460}
                  minWidth={460}
                >
                  {/* Carousel Slides Management */}
                  <CarouselSlidesManager
                    slides={slides}
                    selectedSlidePosition={selectedSlidePosition}
                    onSlidesChange={handleSlidesChange}
                    onSelectedSlideChange={handleSelectedSlideChange}
                  />

                  <Flex
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body-semibold">
                      Slide {selectedSlide.position}
                    </Typography>
                    {slides.length > 1 && (
                      <Button.Icon
                        aria-label="Remove slide"
                        className={styles['remove-slide-button']}
                        icon={<TrashIcon size="sm" />}
                        onClick={() =>
                          handleRemoveSlide(selectedSlide.position)
                        }
                        size="small"
                        variant="tertiary"
                      />
                    )}
                  </Flex>

                  <Flex
                    flexDirection="column"
                    background="grays-white"
                    p={16}
                    gap={16}
                    borderRadius="sm"
                  >
                    <SlideLinkField
                      key={`link-${selectedSlide.position}`}
                      link={selectedSlide.link}
                      onChange={handleLinkChange}
                    />

                    <Form.TextAreaField
                      key={`altText-${selectedSlide.position}`}
                      typography="body-regular"
                      rows={3}
                      name="currentSlideAltText"
                      label="Alt text (Optional)"
                      placeholder={defaultAltText(storePayload?.store.name)}
                      value={selectedSlide.altText}
                      onChange={(value: string) => handleAltTextChange(value)}
                    />
                  </Flex>
                </Flex>

                <Flex flexDirection="column" width="100%" gap={24}>
                  <ImageUploadArea
                    key={`desktop-${selectedSlide.position}-${
                      selectedSlide.desktopImage || 'empty'
                    }`}
                    title="Desktop preview"
                    image={selectedSlide.desktopImage}
                    onImageChange={handleDesktopImageChange}
                    onImageRemove={handleDesktopImageRemove}
                    width={1312}
                    height={320}
                    pagination={
                      slides.length > 1
                        ? `${selectedSlide.position}/${slides.length}`
                        : undefined
                    }
                    onValidationError={setFormError}
                  />

                  <ImageUploadArea
                    key={`mobile-${selectedSlide.position}-${
                      selectedSlide.mobileImage ||
                      selectedSlide.desktopImage ||
                      'empty'
                    }`}
                    title="Mobile preview (Optional)"
                    image={
                      selectedSlide.mobileImage || selectedSlide.desktopImage
                    }
                    onImageChange={handleMobileImageChange}
                    onImageRemove={handleMobileImageRemove}
                    isMobile={true}
                    width={343}
                    height={240}
                    pagination={
                      slides.length > 1
                        ? `${selectedSlide.position}/${slides.length}`
                        : undefined
                    }
                    showFallbackText={
                      !selectedSlide.mobileImage && !!selectedSlide.desktopImage
                    }
                    useFullWidth={true}
                    onValidationError={setFormError}
                  />
                </Flex>
              </Flex>
            ) : (
              <Flex p={32}>
                <Skeleton width="100%" height={400}>
                  Loading...
                </Skeleton>
              </Flex>
            )}
          </Flex>
        </Modal.Content>

        {publishToModalOpen && (
          <StoreSelectModal
            onSearchCallback={(query: string, successful?: boolean) =>
              trackSearch({
                event: EventNames.Search,
                arguments: query,
                subject: SearchSubjects.Stores,
                successful,
              })
            }
            storesData={storesData || []}
            isFetchingStores={isFetchingStores}
            isLoadingStores={isLoadingStores}
            selectedStoreIds={currentStoreSelection}
            disabledStoreIds={currentStoreSelection}
            isSubmitting={isPublishingTo}
            onSubmit={onSelectStores}
            closeModal={() => setPublishToModalClose()}
            onFilterChange={onFilterChange}
            onToggleAll={onToggleAllStores}
            onToggleStore={onToggleStore}
          />
        )}
      </Form.BaseForm>
    </ConfirmWrapperWithTracking>
  );
};
